/*============================================================================*/

/**
 * Event handlers
 *
 * Functions that handle user interactions such as clicks, hovers,
 * and form events. They encapsulate logic triggered by events.
 */

/*============================================================================*/


/**
 * Handles sequential content updates to prevent overlapping requests.
 *
 * - Executes a `loadFunction` to fetch or process new data.
 * - Ensures that only one request runs at a time; the next update
 *   starts only after the previous request has completed.
 * - Supports conditional execution using the `condition` parameter.
 * - Provides lifecycle hooks:
 *   - `beforeLoad` runs before starting the request.
 *   - `onLoadCallback` processes the response data.
 *   - `afterLoad` runs after the update is completed.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.containerSelector - Selector or element for the container.
 * @param {string} options.elementSelector - Selector for elements to check presence before loading.
 * @param {Function} options.loadFunction - Async function that performs the request (should return a Promise/Deferred).
 * @param {Function} options.onLoadCallback - Callback executed with the response once loaded.
 * @param {boolean} [options.condition=false] - Whether to execute load only if elements exist.
 * @param {Array} [options.additionalParams=[]] - Extra parameters passed to `loadFunction`.
 * @param {Function} [options.beforeLoad=() => {}] - Hook executed before starting the request.
 * @param {Function} [options.afterLoad=() => {}] - Hook executed after completing the update.
 * 
 * @returns {Promise|Deferred} A promise-like object from `loadFunction`.
 */
function handleSequentialContentUpdate({
    containerSelector,
    elementSelector,
    loadFunction,
    onLoadCallback,
    condition = false,
    additionalParams = [],
    beforeLoad = () => {},
    afterLoad = () => {}
} = {})
{
    const container = jQuery(containerSelector);
    const elements = container.find(elementSelector);

    const executeLoad = () => {
        beforeLoad();
        return loadFunction(...additionalParams).done((response) => {
            onLoadCallback(response);
            afterLoad();
        });
    };

    if (condition && elements.length > 0)
    {
        return executeLoad();
    }

    return executeLoad();
}

/**
 * Handles infinite scroll requests to dynamically load more content.
 *
 * - Monitors the scroll position relative to a target container.
 * - Triggers the `loadFunction` when the user scrolls near the bottom,
 *   respecting the `offsetThreshold`.
 * - Uses `isLoading` to prevent overlapping or duplicate requests.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.containerSelector - Selector or element for the container.
 * @param {number} [options.offsetThreshold=100] - Distance in pixels from the bottom before triggering load.
 * @param {Function} options.loadFunction - Function to call when threshold is reached (should return a Promise/Deferred).
 * @param {Function} options.isLoading - Function returning `true` if a load request is already in progress.
 */
function handleInfiniteScrollRequests({
    containerSelector,
    offsetThreshold = 100,
    loadFunction,
    isLoading
} = {})
{
    const scrollTop = jQuery(window).scrollTop();
    const windowHeight = jQuery(window).height();
    const container = jQuery(containerSelector);

    if (!container.length) return;

    const containerOffsetTop = container.offset().top;
    const containerHeight = container.outerHeight();

    if (scrollTop + windowHeight >= containerOffsetTop + containerHeight - offsetThreshold)
    {
        if (!isLoading())
        {
            loadFunction();
        }
    }
}

/**
 * Handle redirect after user login
 *
 * Reads the "redirect_after_login" cookie, clears it,
 * and redirects the user either to the stored URL or a fallback.
 */
function handleRedirectAfterLogin()
{
    const match = document.cookie.match(/(?:^|;\s*)redirect_after_login=([^;]+)/)
    const redirectUrl = match ? decodeURIComponent(match[1]) : null

    document.cookie = "redirect_after_login=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;"

    if (redirectUrl && /^https?:\/\//i.test(redirectUrl))
    {
        window.location.href = redirectUrl
    }
    else
    {
        window.location.href = PageURL(APP_HOME_URL)
    }
}
